// Project row in the sidebar: folder icon, expand/collapse, hover ⋮ menu,
// and nested conversation children rendered by the parent Sidebar.
import { useState } from "react";
import { ChevronRight, Folder, FolderOpen, MoreHorizontal, Pencil, Trash2, BookOpen } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { InlineRename } from "./InlineRename";

export function ProjectItem({
  project, collapsed, count, onToggle, onRename, onDelete, onOpenKnowledge, knowledgeCount = 0, children,
}) {
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div>
      <div
        className={[
          "group/proj relative flex items-center rounded-md px-1.5 transition-colors",
          "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
        ].join(" ")}
      >
        {renaming ? (
          <div className="flex w-full items-center gap-2 py-1.5">
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <InlineRename
              testId={`project-rename-input-${project.id}`}
              value={project.name}
              onCommit={(next) => { onRename(project.id, next); setRenaming(false); }}
              onCancel={() => setRenaming(false)}
            />
          </div>
        ) : (
          <>
            <button
              data-testid={`project-toggle-${project.id}`}
              onClick={() => onToggle(project.id)}
              className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md py-1.5 text-left text-[12.5px]"
            >
              <ChevronRight
                className={[
                  "h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-150",
                  collapsed ? "" : "rotate-90",
                ].join(" ")}
              />
              {collapsed ? (
                <Folder className="h-3.5 w-3.5 shrink-0 text-amber-400/80" />
              ) : (
                <FolderOpen className="h-3.5 w-3.5 shrink-0 text-amber-400/80" />
              )}
              <span className="truncate font-medium">{project.name}</span>
              {typeof count === "number" && (
                <span className="ml-auto text-[10.5px] tabular-nums text-muted-foreground/70">
                  {count}
                </span>
              )}
            </button>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`project-menu-${project.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className={[
                    "ml-1 rounded p-1 text-muted-foreground transition-opacity hover:bg-background hover:text-foreground",
                    menuOpen ? "opacity-100" : "opacity-0 group-hover/proj:opacity-100",
                  ].join(" ")}
                  aria-label="Project actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  data-testid={`project-knowledge-${project.id}`}
                  onClick={() => onOpenKnowledge?.(project.id)}
                  className="text-[12.5px]"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Knowledge base</span>
                  {knowledgeCount > 0 && (
                    <span className="ml-auto rounded-full bg-primary/15 px-1.5 py-0.5 text-[9.5px] font-medium text-primary">
                      {knowledgeCount}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`project-rename-${project.id}`}
                  onClick={() => setRenaming(true)}
                  className="text-[12.5px]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid={`project-delete-${project.id}`}
                  onClick={() => onDelete(project.id)}
                  className="text-[12.5px] text-rose-400 focus:text-rose-300"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
      {!collapsed && (
        <div className="mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
